import type {
  FoodpartyListing,
  FoodpartyReservation,
  SavedFoodReport,
  SurplusSuggestion,
} from "@kitchen/schemas";
import { formatMoney } from "@kitchen/ui";
import { useEffect, useState } from "react";
import {
  closeFoodpartyListing,
  createFoodpartyListing,
  deliverFoodpartyReservation,
  getSavedFoodReport,
  listFoodpartyListings,
  listFoodpartyReservations,
  listFoodpartySurplusSuggestions,
  releaseExpiredFoodpartyReservations,
  triggerFoodpartySafetyCheck,
} from "../../api.js";
import { SavedFoodReportPrint } from "./SavedFoodReportPrint.js";

type Tab = "suggestions" | "listings" | "counter" | "report";

export function FoodpartyPage() {
  const [tab, setTab] = useState<Tab>("listings");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  // Data states
  const [suggestions, setSuggestions] = useState<SurplusSuggestion[]>([]);
  const [listings, setListings] = useState<FoodpartyListing[]>([]);
  const [reservations, setReservations] = useState<FoodpartyReservation[]>([]);
  const [report, setReport] = useState<SavedFoodReport | null>(null);

  // New listing form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [foodName, setFoodName] = useState("");
  const [foodId, setFoodId] = useState("018f2d5e-0000-7000-8000-000000000001");
  const [portions, setPortions] = useState(10);
  const [originalPriceToman, setOriginalPriceToman] = useState(120_000);
  const [discountType, setDiscountType] = useState<"percent" | "fixed_amount">("percent");
  const [discountValue, setDiscountValue] = useState(40);
  const [source, setSource] = useState<
    "manual" | "production_overrun" | "late_cancellation" | "undelivered_takeout"
  >("manual");
  const [sourceRefId, setSourceRefId] = useState<string | null>(null);

  // Counter delivery state
  const [deliveryCodeInput, setDeliveryCodeInput] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "suggestions") {
        const data = await listFoodpartySurplusSuggestions();
        setSuggestions(data);
      } else if (tab === "listings") {
        const data = await listFoodpartyListings();
        setListings(data);
      } else if (tab === "counter") {
        const data = await listFoodpartyReservations({ status: "reserved" });
        setReservations(data);
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const data = await getSavedFoodReport(today, today);
        setReport(data);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "خطا در بارگذاری اطلاعات فودپارتی";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [tab]);

  const handleOpenFromSuggestion = (s: SurplusSuggestion) => {
    setFoodId(s.foodId);
    setFoodName(s.foodName);
    setPortions(s.portions);
    setOriginalPriceToman(Math.round(Number(s.originalPriceRial) / 10));
    setDiscountType("percent");
    setDiscountValue(s.suggestedDiscountPercent);
    setSource(s.source);
    setSourceRefId(s.sourceRefId);
    setShowAddModal(true);
  };

  const handleCreateListing = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!foodName.trim() || portions <= 0) return;
    setLoading(true);
    setError(null);
    try {
      await createFoodpartyListing({
        foodId,
        foodName: foodName.trim(),
        portions,
        originalPriceRial: originalPriceToman * 10,
        discountType,
        discountValue,
        cookedAt: new Date().toISOString(),
        source,
        sourceRefId,
      });
      setShowAddModal(false);
      setMessage("آگهی فودپارتی با موفقیت ثبت شد.");
      setTab("listings");
      await refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در ثبت آگهی";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseListing = async (id: string) => {
    if (!confirm("آیا از بستن این آگهی مطمئن هستید؟")) return;
    try {
      await closeFoodpartyListing(id);
      setMessage("آگهی بسته شد.");
      await refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در بستن آگهی";
      setError(msg);
    }
  };

  const handleDeliver = async (code: string) => {
    if (!code || code.length !== 6) {
      setError("کد تحویل باید ۶ رقم باشد.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await deliverFoodpartyReservation({ deliveryCode: code });
      setMessage(`سفارش به شماره کد ${res.deliveryCode} با موفقیت تحویل و تسویه شد.`);
      setDeliveryCodeInput("");
      await refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "کد نامعتبر است یا سفارش قبلاً تحویل شده است.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseExpired = async () => {
    setLoading(true);
    try {
      const res = await releaseExpiredFoodpartyReservations();
      setMessage(`${String(res.released)} رزرو منقضی آزاد شد و پرس‌ها به مازاد برگشت داده شد.`);
      await refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در آزادسازی رزروهای منقضی";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSafetyCheck = async () => {
    setLoading(true);
    try {
      const res = await triggerFoodpartySafetyCheck();
      setMessage(`${String(res.expired)} آگهی که از سقف ایمنی ۴ ساعت گذشته بودند منقضی شدند.`);
      await refresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در بررسی ایمنی غذا";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">فودپارتی (فروش مازاد غذا)</h1>
          <p className="text-sm text-gray-500 mt-1">
            فروش غذای باکیفیت مازاد با تخفیف به عموم جهت جلوگیری از اسراف (§۱۲)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setFoodId("018f2d5e-0000-7000-8000-000000000001");
              setFoodName("");
              setPortions(10);
              setOriginalPriceToman(120_000);
              setDiscountValue(40);
              setSource("manual");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-emerald-700 text-white font-medium rounded-xl hover:bg-emerald-800 transition"
          >
            + ثبت آگهی مازاد جدید
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSafetyCheck();
            }}
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm"
            title="بررسی مهلت ۴ ساعته ایمنی پخت تا فروش"
          >
            بررسی ایمنی ۴ ساعته
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="p-4 bg-rose-50 text-rose-800 border border-rose-200 rounded-xl text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => {
              setError(null);
            }}
            className="font-bold"
          >
            ×
          </button>
        </div>
      )}
      {message && (
        <div className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-sm flex items-center justify-between">
          <span>{message}</span>
          <button
            type="button"
            onClick={() => {
              setMessage(null);
            }}
            className="font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 gap-2">
        <button
          type="button"
          onClick={() => {
            setTab("listings");
          }}
          className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
            tab === "listings"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          آگهی‌های فعال و پیشین
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("suggestions");
          }}
          className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
            tab === "suggestions"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          پیشنهادهای مازاد آشپزخانه
        </button>
        <button
          id="tab-btn-counter"
          type="button"
          onClick={() => {
            setTab("counter");
          }}
          className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
            tab === "counter"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          پیشخوان تحویل و تسویه
        </button>
        <button
          id="tab-btn-report"
          type="button"
          onClick={() => {
            setTab("report");
          }}
          className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
            tab === "report"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          گزارش غذای نجات‌یافته
        </button>
      </div>

      {/* TAB 1: Listings */}
      {tab === "listings" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 font-medium">تعداد کل آگهی‌ها: {listings.length}</span>
            <button
              type="button"
              onClick={() => {
                void refresh();
              }}
              className="text-sm text-emerald-700 hover:underline"
            >
              به‌روزرسانی فهرست
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-right text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                <tr>
                  <th className="p-3">نام غذا</th>
                  <th className="p-3">موجودی / کل</th>
                  <th className="p-3">رزرو / فروش</th>
                  <th className="p-3">قیمت اصلی</th>
                  <th className="p-3">قیمت با تخفیف</th>
                  <th className="p-3">مهلت تحویل</th>
                  <th className="p-3">وضعیت</th>
                  <th className="p-3">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-gray-400">
                      هیچ آگهی‌ای ثبت نشده است.
                    </td>
                  </tr>
                ) : (
                  listings.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="p-3 font-bold text-gray-800">{l.foodName}</td>
                      <td className="p-3">
                        <span className="font-bold text-emerald-700">{l.portionsAvailable}</span>
                        <span className="text-gray-400"> / {l.portionsTotal}</span>
                      </td>
                      <td className="p-3 text-gray-600">
                        {l.portionsReserved} رزرو / {l.portionsSold} تحویل
                      </td>
                      <td className="p-3 text-gray-400 line-through">
                        {formatMoney(Number(l.originalPriceRial))}
                      </td>
                      <td className="p-3 font-bold text-emerald-700">
                        {formatMoney(Number(l.finalPriceRial))}
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {new Date(l.pickupDeadline).toLocaleTimeString("fa-IR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                            l.status === "active"
                              ? "bg-emerald-100 text-emerald-800"
                              : l.status === "depleted"
                                ? "bg-amber-100 text-amber-800"
                                : l.status === "expired"
                                  ? "bg-rose-100 text-rose-800"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {l.status === "active"
                            ? "فعال"
                            : l.status === "depleted"
                              ? "تکمیل شد"
                              : l.status === "expired"
                                ? "منقضی"
                                : "بسته‌شده"}
                        </span>
                      </td>
                      <td className="p-3">
                        {l.status === "active" && (
                          <button
                            type="button"
                            onClick={() => {
                              void handleCloseListing(l.id);
                            }}
                            className="px-2 py-1 text-xs border border-rose-300 text-rose-700 rounded-lg hover:bg-rose-50"
                          >
                            بستن
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Suggestions */}
      {tab === "suggestions" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            سیستم بر اساس پخت مازاد بر سفارش و لغوهای دیرهنگام، موارد زیر را جهت ارسال به فودپارتی پیشنهاد
            می‌کند:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.length === 0 ? (
              <div className="col-span-full p-8 text-center text-gray-400 bg-white rounded-2xl border">
                در حال حاضر هیچ پیشنهاد مازادی از سفارش‌ها یا آشپزخانه وجود ندارد.
              </div>
            ) : (
              suggestions.map((s) => (
                <div
                  key={s.id}
                  className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-800 text-base">{s.foodName}</span>
                      <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                        {s.source === "production_overrun"
                          ? "بیش‌پخت دیگ"
                          : s.source === "late_cancellation"
                            ? "لغو دیرهنگام"
                            : "بیرون‌بر"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{s.details}</p>
                    <div className="pt-2 text-sm">
                      <span className="font-bold text-emerald-700 text-lg">{s.portions} پرس </span>
                      <span className="text-gray-500">(پیشنهاد {s.suggestedDiscountPercent}٪ تخفیف)</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      handleOpenFromSuggestion(s);
                    }}
                    className="mt-4 w-full py-2 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-800 transition"
                  >
                    ارسال به فودپارتی با یک کلیک
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* TAB 3: Counter */}
      {tab === "counter" && (
        <div className="space-y-6">
          {/* Quick deliver by 6-digit code */}
          <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900">تحویل سفارش در پیشخوان با کد ۶ رقمی</h2>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                id="counter-delivery-code"
                type="text"
                placeholder="کد ۶ رقمی تحویل (مثال: ۱۲۳۴۵۶)"
                maxLength={6}
                value={deliveryCodeInput}
                onChange={(e) => {
                  setDeliveryCodeInput(e.target.value);
                }}
                className="px-4 py-3 border border-gray-300 rounded-xl text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-600"
              />
              <button
                id="counter-btn-deliver"
                type="button"
                onClick={() => {
                  void handleDeliver(deliveryCodeInput);
                }}
                disabled={loading || deliveryCodeInput.length !== 6}
                className="px-6 py-3 bg-emerald-700 text-white font-bold rounded-xl hover:bg-emerald-800 disabled:opacity-50 transition"
              >
                تأیید تحویل و تسویه
              </button>
            </div>
          </div>

          {/* Reserved orders list */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-gray-800">
                رزروهای در انتظار تحویل ({reservations.length})
              </h3>
              <button
                type="button"
                onClick={() => {
                  void handleReleaseExpired();
                }}
                className="text-xs px-3 py-1.5 border border-amber-300 text-amber-800 bg-amber-50 rounded-xl hover:bg-amber-100"
              >
                آزادسازی خودکار رزروهای منقضی
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <table className="w-full text-right text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                  <tr>
                    <th className="p-3">کد تحویل</th>
                    <th className="p-3">مشتری</th>
                    <th className="p-3">تعداد</th>
                    <th className="p-3">مبلغ تسویه</th>
                    <th className="p-3">مهلت تحویل</th>
                    <th className="p-3">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reservations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-gray-400">
                        هیچ رزروی در انتظار تحویل نیست.
                      </td>
                    </tr>
                  ) : (
                    reservations.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="p-3 font-mono text-base font-bold text-emerald-800">
                          {r.deliveryCode}
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-gray-800">{r.customerName || "مشتری"}</span>
                          <span className="block text-xs text-gray-400 font-mono">{r.customerPhone}</span>
                        </td>
                        <td className="p-3 font-bold">{r.portions} پرس</td>
                        <td className="p-3 font-bold text-emerald-700">
                          {formatMoney(Number(r.totalPriceRial))}
                        </td>
                        <td className="p-3 text-xs text-gray-500">
                          {new Date(r.pickupDeadline).toLocaleTimeString("fa-IR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => {
                              void handleDeliver(r.deliveryCode);
                            }}
                            className="px-3 py-1 bg-emerald-700 text-white text-xs font-semibold rounded-lg hover:bg-emerald-800"
                          >
                            تحویل شد
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: Report */}
      {tab === "report" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-white p-4 rounded-2xl border border-gray-200">
            <div>
              <h2 className="text-base font-bold text-gray-900">گزارش آماری و ریالی غذای نجات‌یافته</h2>
              <p className="text-xs text-gray-500 mt-1">آمار تجمعی غذاهای مازاد پیشگیری‌شده از اسراف</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowPrint(true);
              }}
              className="px-4 py-2 bg-slate-800 text-white text-sm font-semibold rounded-xl hover:bg-slate-900 flex items-center gap-2"
            >
              <span>🖨️</span>
              <span>چاپ فرم رسمی A4</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-2xl">
              <span className="text-xs text-emerald-700 font-semibold">تعداد پرس‌های نجات‌یافته</span>
              <p className="text-2xl font-bold text-emerald-900 mt-2">
                {report?.totalPortionsSaved ?? 0} پرس
              </p>
            </div>
            <div className="p-5 bg-blue-50 border border-blue-200 rounded-2xl">
              <span className="text-xs text-blue-700 font-semibold">درآمد حاصل از فروش مازاد</span>
              <p className="text-2xl font-bold text-blue-900 mt-2">
                {formatMoney(Number(report?.totalSavedRevenueRial ?? 0))}
              </p>
            </div>
            <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl">
              <span className="text-xs text-amber-700 font-semibold">ارزش واقعی غذای نجات‌یافته</span>
              <p className="text-2xl font-bold text-amber-900 mt-2">
                {formatMoney(Number(report?.totalOriginalValueRial ?? 0))}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-right text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                <tr>
                  <th className="p-3">تاریخ</th>
                  <th className="p-3">نام غذا</th>
                  <th className="p-3">پرس نجات‌یافته</th>
                  <th className="p-3">درآمد حاصله</th>
                  <th className="p-3">ارزش اولیه</th>
                  <th className="p-3">تخفیف داده‌شده</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report?.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-gray-400">
                      هیچ داده‌ای برای این بازه ثبت نشده است.
                    </td>
                  </tr>
                ) : (
                  report?.rows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-3 font-mono text-gray-600">{row.date}</td>
                      <td className="p-3 font-bold text-gray-800">{row.foodName}</td>
                      <td className="p-3 font-bold text-emerald-700">{row.portionsSaved}</td>
                      <td className="p-3">{formatMoney(Number(row.savedRevenueRial))}</td>
                      <td className="p-3 text-gray-400 line-through">
                        {formatMoney(Number(row.originalValueRial))}
                      </td>
                      <td className="p-3 text-amber-700 font-medium">
                        {formatMoney(Number(row.discountGivenRial))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Create Foodparty Listing */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-5">
            <div className="flex justify-between items-center border-b pb-3">
              <h2 className="text-lg font-bold text-gray-900">ثبت آگهی مازاد فودپارتی</h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => {
                void handleCreateListing(e);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">نام غذا</label>
                <input
                  id="listing-food-name"
                  type="text"
                  required
                  value={foodName}
                  onChange={(e) => {
                    setFoodName(e.target.value);
                  }}
                  placeholder="مثال: زرشک پلو با مرغ"
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">تعداد پرس مازاد</label>
                  <input
                    id="listing-portions"
                    type="number"
                    min={1}
                    required
                    value={portions}
                    onChange={(e) => {
                      setPortions(Number(e.target.value));
                    }}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">قیمت معمول (تومان)</label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    required
                    value={originalPriceToman}
                    onChange={(e) => {
                      setOriginalPriceToman(Number(e.target.value));
                    }}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">نوع تخفیف</label>
                  <select
                    value={discountType}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "percent" || val === "fixed_amount") {
                        setDiscountType(val);
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  >
                    <option value="percent">درصدی (٪)</option>
                    <option value="fixed_amount">مبلغ ثابت (ریال)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {discountType === "percent" ? "درصد تخفیف (۱ تا ۹۹)" : "مبلغ تخفیف (ریال)"}
                  </label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={discountValue}
                    onChange={(e) => {
                      setDiscountValue(Number(e.target.value));
                    }}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="p-3 bg-emerald-50 text-emerald-900 rounded-xl text-xs space-y-1">
                <span className="font-bold">قیمت نهایی فروش هر پرس: </span>
                <span className="text-sm font-bold text-emerald-700">
                  {formatMoney(
                    (discountType === "percent"
                      ? Math.round((originalPriceToman * (100 - discountValue)) / 100)
                      : Math.round(originalPriceToman - discountValue / 10)) * 10,
                  )}
                </span>
                <p className="text-gray-500">
                  مهلت ایمنی مصرف غذا حداکثر ۴ ساعت از زمان پخت است و پس از آن آگهی خودکار منقضی می‌شود.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  className="px-4 py-2 border text-gray-600 rounded-xl text-sm hover:bg-gray-50"
                >
                  انصراف
                </button>
                <button
                  id="btn-submit-listing"
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2 bg-emerald-700 text-white rounded-xl text-sm font-bold hover:bg-emerald-800 disabled:opacity-50"
                >
                  ثبت آگهی
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* A4 Print Modal */}
      {showPrint && report && (
        <SavedFoodReportPrint
          report={report}
          onClose={() => {
            setShowPrint(false);
          }}
        />
      )}
    </div>
  );
}
